import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import AppDateTimePicker from "../../components/Core/AppDateTimePicker/AppDateTimePicker";
import AppText from "../../components/Core/AppText/AppText";
import ProfilePicture from "../../components/Core/ProfilePicture/ProfilePicture";
import SelectPopover from "../../components/Core/SelectPopover/SelectPopover";
import Flex from "../../components/Layout/Flex";
import Breadcrumbs, { BreadcrumbItem } from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import DocumentTab from "../../components/Shared/DocumentTab/DocumentTab";
import InfoBlock from "../../components/Shared/InfoBlock/InfoBlock";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import RecruitmentSendMessageModal from "../../components/Shared/Recruitment/RecruitmentSendMessageModal";
import RecruitmentStatusModal from "../../components/Shared/Recruitment/RecruitmentStatusModal";
import SendContractModal from "../../components/Shared/SendContractModal";
import SimpleConfirmationModal from "../../components/Shared/SimpleConfirmationModal/SimpleConfirmationModal";
import DetailPageTabs, {
	RouteTabItem,
	ListToolbarAction,
} from "../../components/Shared/DetailPageTabs";
import TagToggleSubMenu from "../../components/Shared/TagMenu/TagToggleSubMenu";
import { RecruitmentBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { RecruitmentActionType } from "../../configs/constants/RecruitmentActionType";
import { RecruitmentStatusType } from "../../configs/constants/RecruitmentStatusType";
import { routes } from "../../configs/constants/routes";
import { RECRUITMENT_ACTION_KEY_VALUES } from "../../configs/recruitmentActions.configs";
import { Documents } from "../../models/Consultant";
import { InviteOption } from "../../models/InternalUser";
import { KeyValue } from "../../models/KeyValue";
import {
	useCandidate,
	useDeleteCandidate,
	useRecallAgreement,
	useRecruitCandidate,
	useSendRecruitmentContract,
	useUpdateCandidate,
	useUpdateCandidateRecruitmentDetails,
} from "../../networking/candidate.hooks";
import { ConfirmModalProps } from "../../stores/confirmModal.store";
import { useOrganisation } from "../../networking/organisation.hooks";
import { toggleConsultantTag } from "../../utils/credential.utils";
import { getSignedContractStatus } from "../../utils/recruitment.utils";
import { formatRoute } from "../../utils/route.utils";
import { generateAvatarString } from "../../utils/string.utils";
import ConsultantHistoryTab from "../Consultants/ConsultantHistoryTab";
import RecruitmentCompetenceTab from "./RecruitmentCompetenceTab";
import RecruitmentEmpAndSalaryTab from "./RecruitmentEmpAndSalaryTab";
import RecruitmentHistoryTab from "./RecruitmentHistoryTab";
import RecruitmentPresentationTab from "./RecruitmentPresentationTab";
import RecruitmentProfileTab from "./RecruitmentProfileTab";
import { toast } from "../../utils/toast";
import { useConfirmModalStore } from "../../stores/confirmModal.store";

interface ConsultantsDetailsParamProps {
	candidateId: string;
}
export interface DetailWrapperStyleProps {
	isHide: boolean;
}

const RecruitmentDetailsPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { candidateId } = useParams<
		keyof ConsultantsDetailsParamProps
	>() as ConsultantsDetailsParamProps;
	const { candidate, isLoading } = useCandidate(candidateId);
	const { data: organisation } = useOrganisation();
	const { mutate: updateCandidate } = useUpdateCandidate();
	const { mutate: updateCandidateRecruitmentDetails } = useUpdateCandidateRecruitmentDetails();
	const { mutate: recruitCandidate } = useRecruitCandidate();
	const { mutate: sendContract } = useSendRecruitmentContract();
	const { mutateAsync: deleteCandidate } = useDeleteCandidate();
	const { mutateAsync: recallAgreement } = useRecallAgreement();

	const modalStore = useConfirmModalStore();

	// const [age, setAge] = useState<number>();
	const [showStatusChangeModal, setShowStatusChangeModal] = useState<boolean>();
	const [showSendMessageModal, setShowSendMessageModal] = useState<boolean>();
	const [canMoveToConsultantPool, setCanMoveToConsultantPool] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [openMoveToConsultantModal, setOpenMoveToConsultantModal] = useState<boolean>(false);
	const [isSendContractModalOpen, setSendContractModalOpen] = useState<boolean>(false);

	const updateDocuments = async (documents: Documents) => {
		return await updateCandidate({
			userId: candidateId,
			update: {
				profile: { documents },
			},
		});
	};

	const showMessageModal = () => {
		setShowSendMessageModal(true);
	};

	const hideMessageModal = () => {
		setShowSendMessageModal(false);
	};

	const handleMoveToConsultant = useCallback(async () => {
		setOpenMoveToConsultantModal(true);
	}, [setOpenMoveToConsultantModal]);

	const handleSendContract = async () => {
		if (
			!candidate?.id ||
			!((candidate.profile.employment?.salary || [])[0] || {}).amount ||
			!candidate.profile.socialSecurityNumber
		) {
			return toast.error(t("recruitment.sendContractModal.missingDetails"));
		}

		setSendContractModalOpen(true);
	};

	const handleRevokeContract = async () => {
		if (
			!candidate?.id ||
			!((candidate.profile.employment?.salary || [])[0] || {}).amount ||
			!candidate.profile.socialSecurityNumber
		) {
			return toast.error(t("recruitment.sendContractModal.missingDetails"));
		}

		recallAgreement({ userId: candidate.id });
	};

	const handleMoveToConsultantCancel = useCallback(async () => {
		setOpenMoveToConsultantModal(false);
	}, [setOpenMoveToConsultantModal]);

	const handleMoveToConsultantConfirm = useCallback(async () => {
		if (candidate?.id) {
			setLoading(true);

			await recruitCandidate({ userId: candidate.id });

			setLoading(false);
			setOpenMoveToConsultantModal(false);
		}
	}, [candidate]);

	const canRecallContract =
		candidate?.profile?.recruitment?.currentAction === "AGREEMENT_SENT" &&
		(candidate?.profile?.employment?.employmentContract?.length || 0) > 0;

	const candidateActions: ListToolbarAction[] = [
		{
			label: "SEND_MESSAGE_ACTION",
			onClick: showMessageModal,
		},
		{
			label: "MOVE_TO_CONSULTANT_POOL",
			onClick: handleMoveToConsultant,
			disabled: !canMoveToConsultantPool || loading,
		},
		{
			label: canRecallContract ? "Återkalla avtal" : "SEND_CONTRACT",
			onClick: canRecallContract ? handleRevokeContract : handleSendContract,
			disabled: loading,
		},
		{
			label: "recruitment.removeCandidate",
			danger: true,
			dividerBefore: true,
			onClick: () => deleteCandidateConfirm(),
		},
	];

	const deleteCandidateConfirm = () => {
		if (candidate) {
			const deleteConfirmModal: ConfirmModalProps = {
				title: "DELETE_CONSULTANT",
				text: "DELETE_CONSULTANT_TEXT",
				acceptButtonText: "YES",
				rejectButtonText: "NO",
				onAccept: async () => {
					await deleteCandidate({ userId: candidate.id }).then(() => navigate(-1));
				},
				onReject: () => {},
				type: "DANGER",
			};
			modalStore.newModal(deleteConfirmModal);
		}
	};

	const navigateToRecruitmentDetails = () => {
		return formatRoute([routes.RECRUITMENT_DETAILS, routes.CONSULTANTS_PROFILE].join("/"), [
			candidateId,
		]);
	};

	const RecruitmentDetailsBreadcrumbs: BreadcrumbItem[] = [
		...RecruitmentBreadcrumbs,
		{
			title: `${candidate?.firstName} ${candidate?.lastName} `,
			path: navigateToRecruitmentDetails(),
		},
	];

	// useEffect(() => {
	// 	if (candidate?.profile.socialSecurityNumber)
	// 		setAge(getAgeBySNN(candidate?.profile.socialSecurityNumber));
	// }, [candidate]);

	const showStatusModal = () => {
		setShowStatusChangeModal(true);
	};

	const hideStatusModal = () => {
		setShowStatusChangeModal(false);
	};

	const handleCurrentAction = async (currentAction: RecruitmentActionType) => {
		if (candidate)
			await updateCandidateRecruitmentDetails({
				userId: candidate.id,
				update: { currentAction },
			});
	};

	const handleNextActionDate = async (nextActionTime: Date) => {
		if (candidate)
			await updateCandidateRecruitmentDetails({
				userId: candidate.id,
				update: { nextActionTime },
			});
	};

	const handleUploadProfilePicture = useCallback(
		async (pic: any) => {
			if (candidate?.id) {
				await updateCandidate({
					userId: candidate.id,
					update: {
						profile: {
							profilePicture: pic || "",
						},
					},
				});
			}
		},
		[candidate]
	);

	useEffect(() => {
		if (
			!candidate?.profile.recruitment?.isRecruited &&
			candidate?.profile.recruitment?.status === RecruitmentStatusType.HIRED
		) {
			setCanMoveToConsultantPool(true);
		} else {
			setCanMoveToConsultantPool(false);
		}
	}, [candidate, setCanMoveToConsultantPool]);

	if (isLoading) {
		return <LoadingIndicator />;
	}

	if (!candidate) return null;

	const consultantTagTypes = organisation?.consultantTagTypes ?? [];

	// Toggle via the plain candidate update (no optimistic cache patching like the
	// consultant page — the ["candidates", id] invalidation refetches the header).
	const handleToggleTag = (key: string) => {
		const type = consultantTagTypes.find((tagType) => tagType.key === key);
		if (!type) return;
		updateCandidate({
			userId: candidate.id,
			update: {
				profile: { tags: toggleConsultantTag(candidate.profile?.tags ?? [], type) },
			},
		});
	};

	const CONSULTANTS_TAB_GROUPS: RouteTabItem[] = [
		{
			route: routes.RECRUITMENT_PROFILE,
			label: "PROFILE",
			content: <RecruitmentProfileTab candidate={candidate} />,
		},
		{
			route: routes.RECRUITMENT_DOCUMENTS,
			label: "DOCUMENTS",
			content: (
				<DocumentTab
					documents={candidate.profile.documents}
					updateDocuments={updateDocuments}
					isCandidate={true}
				/>
			),
		},
		{
			route: routes.RECRUITMENT_PRESENTATION,
			label: "PUBLIC_PRESENTATION",
			content: <RecruitmentPresentationTab candidate={candidate} />,
		},
		{
			route: routes.RECRUITMENT_COMPETENCE,
			label: "COMPETENCE",
			content: <RecruitmentCompetenceTab candidate={candidate} />,
		},
		{
			route: routes.RECRUITMENT_EMPLOYMENT_AND_SALARY,
			label: "EMPLOYMENT_AND_SALARY",
			content: <RecruitmentEmpAndSalaryTab candidate={candidate} />,
		},
		{
			route: routes.RECRUITMENT_HISTORY,
			label: "HISTORY",
			content: <RecruitmentHistoryTab candidate={candidate} />,
		},
		{
			// Candidates are consultant accounts with `profile.recruitment` set, so
			// the consultant history endpoint and tab serve them unchanged. This
			// sits alongside HISTORY, which is the recruitment-pipeline log.
			route: routes.RECRUITMENT_EVENTS,
			label: "EVENTS",
			content: <ConsultantHistoryTab consultant={candidate} />,
		},
	];

	return (
		<>
			<Breadcrumbs
				items={RecruitmentDetailsBreadcrumbs}
				statusItem={{
					status: candidate?.profile?.recruitment?.status as string,
					action: showStatusModal,
				}}
			/>
			<div className="p-4 px-6" key={candidate?.id}>
				{showStatusChangeModal && (
					<RecruitmentStatusModal hideModal={hideStatusModal} candidate={candidate} />
				)}
				{showSendMessageModal && (
					<RecruitmentSendMessageModal hideModal={hideMessageModal} candidate={candidate} />
				)}
				{/* This is hide when loading if not the unmounted component will lead to a state update warning */}
				{/* This is related to recruitment competence tab state updating */}
				<DetailWrapper isHide={isLoading}>
					<Flex width="100%" maxWidth="1200px">
						<Flex width="140px" margin="0 1.5rem 0 0">
							<ProfilePicture
								name={generateAvatarString(`${candidate?.firstName} ${candidate?.lastName}`)}
								onUploadProfilePicture={handleUploadProfilePicture}
							/>
						</Flex>

						<Flex direction="row" gap="0 2vw" flexWrap="wrap" width="100%" alignItems="flex-start">
							<InfoBlock
								width="18%"
								label="PHONE"
								value={candidate?.profile?.phoneNumber || "-"}
								href={
									candidate?.profile?.phoneNumber
										? `tel:${candidate?.profile?.phoneNumber}`
										: undefined
								}
							/>

							<InfoBlock
								width="18%"
								label="EMAIL"
								value={`${candidate?.email}`}
								href={`mailto:${candidate?.email}`}
							/>

							<Flex width="18%" direction="column">
								<AppText margin="0 0 0.25rem 0" weight="bold" color="grayTextDark">
									{t("CURRENT_ACTION")}
								</AppText>
								<Flex>
									<SelectPopover<KeyValue>
										label="Status"
										popoverWidth={300}
										value={candidate?.profile.recruitment?.currentAction}
										onSelect={(val) => handleCurrentAction(val.key as RecruitmentActionType)}
										options={RECRUITMENT_ACTION_KEY_VALUES}
										popoverPosition="right"
										getLabelFromOption={(o) => (o ? t(o?.value as any) : "")}
										getOptionFromValue={(val, options) => options.find((o) => o.key === val)}
										getValueFromOption={(o) => (o?.key as string) || ""}
										hideLabelAsPrefix
									/>
								</Flex>
							</Flex>

							<Flex width="18%" direction="column">
								<AppText margin="0 0 0.25rem 0" weight="bold" color="grayTextDark">
									{t("NEXT_ACTION_DATE")}
								</AppText>
								<AppDateTimePicker
									onChange={handleNextActionDate}
									placeholder={t("DATE_PICKER_PLACEHOLDER")}
									containerMargin="0"
									value={
										candidate?.profile.recruitment?.nextActionTime
											? new Date(candidate.profile.recruitment.nextActionTime)
											: undefined
									}
									renderInput={(inputValue, onOpen) => (
										<Flex onClick={() => onOpen()} cursor="pointer">
											{/* <AppIcon icon="calendar" margin="0 6px 0 0" size="14px" color="#bbb" /> */}
											<AppText textDecoration="underline">
												{inputValue || t("DATE_PICKER_PLACEHOLDER")}
											</AppText>
										</Flex>
									)}
								/>
							</Flex>
							<InfoBlock width="18%" label="POOL" value={candidate?.profile?.pool || "-"} />

							<InfoBlock
								label="CONTRACT"
								width="20%"
								value={
									getSignedContractStatus(candidate?.profile.employment?.employmentContract, t) ||
									"-"
								}
							/>
						</Flex>
					</Flex>
					<DetailPageTabs
						tabs={CONSULTANTS_TAB_GROUPS}
						actions={candidateActions}
						actionsExtraItems={
							consultantTagTypes.length > 0 && (
								<TagToggleSubMenu
									tagTypes={consultantTagTypes}
									assignedTags={candidate.profile?.tags ?? []}
									onToggle={handleToggleTag}
								/>
							)
						}
					/>
					{openMoveToConsultantModal && (
						<SimpleConfirmationModal
							bodyText={t("recruitment.moveToConsultantPool.confirmationText")}
							onCancel={handleMoveToConsultantCancel}
							onConfirmation={handleMoveToConsultantConfirm}
							isLoading={loading}
							maxWidth="545px"
							confirmButtonText={t("recruitment.moveToConsultantPool.yes")}
							cancelButtonText={t("recruitment.moveToConsultantPool.cancel")}
						/>
					)}
					{isSendContractModalOpen && (
						<SendContractModal
							title={t("recruitment.sendContractModal.title", { name: candidate?.firstName })}
							acceptButtonText="SEND"
							rejectButtonText="CANCEL"
							onAccept={(inviteOptionData: InviteOption, expireDate: string) => {
								if (candidate) {
									sendContract({
										userId: candidate.id,
										sms: inviteOptionData.sms,
										mail: true,
										expireDate: expireDate,
									});
								}
								setSendContractModalOpen(false);
							}}
							onReject={() => setSendContractModalOpen(false)}
							text={t("recruitment.sendContractModal.text")}
							email={candidate?.email}
							phoneNo={candidate?.profile?.phoneNumber}
						/>
					)}
				</DetailWrapper>
			</div>
		</>
	);
};

export default RecruitmentDetailsPage;

const DetailWrapper = styled.div<DetailWrapperStyleProps>`
	display: ${(props) => (props.isHide ? "none" : "block")};
	padding-bottom: 4rem;
`;
